import { useState } from 'react';
import { motion } from 'framer-motion';
import { Video, FileText, StickyNote, CheckCircle2, Circle, PlayCircle } from 'lucide-react';
import { CoachContentViewerModal } from '@/components/CoachContentViewerModal';
import { useLessonBundle, type LessonContentItem } from '@/hooks/useLessonBundle';

const TYPE_ICON = { video: Video, article: FileText, note: StickyNote } as const;
const TYPE_LABEL = { video: 'Video', article: 'Article', note: 'Resource' } as const;

type Props = {
  lessonId: string;
  /** Reported whenever progress changes so the parent can gate the quiz. */
  onProgress?: (info: { requiredTotal: number; requiredDone: number; total: number; done: number }) => void;
};

/**
 * The coach-added content inside a lesson — rendered inline as part of the
 * lesson itself, in the coach's chosen order. There is no separate
 * "Additional Content" destination anymore.
 */
export function LessonContentSection({ lessonId, onProgress }: Props) {
  const { bundle, loading, reload } = useLessonBundle(lessonId);
  const [viewing, setViewing] = useState<LessonContentItem | null>(null);

  const items = bundle.items ?? [];
  const done = items.filter((i) => i.viewed_at).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  // Report progress upward (cheap enough to do on each render of new data)
  const key = `${bundle.required_done}/${bundle.required_total}/${done}/${items.length}`;
  const [lastKey, setLastKey] = useState('');
  if (!loading && key !== lastKey) {
    setLastKey(key);
    onProgress?.({
      requiredTotal: bundle.required_total,
      requiredDone: bundle.required_done,
      total: items.length,
      done,
    });
  }

  if (loading || items.length === 0) return null;

  const currentIndex = items.findIndex((i) => !i.viewed_at);

  return (
    <div className="bg-card rounded-2xl border-2 border-border p-4 shadow-card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold text-sm">Lesson Content</h3>
        <span className="text-[11px] font-display font-bold text-muted-foreground">
          {done} of {items.length} completed — {pct}%
        </span>
      </div>

      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <motion.div
          className="h-full bg-primary"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      <div className="space-y-1.5">
        {items.map((item, idx) => {
          const Icon = TYPE_ICON[item.type] ?? FileText;
          const isDone = !!item.viewed_at;
          const isCurrent = idx === currentIndex;
          return (
            <button
              key={item.item_id}
              onClick={() => setViewing(item)}
              className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${
                isCurrent ? 'border-primary/50 bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
            >
              {isDone ? (
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              ) : isCurrent ? (
                <PlayCircle className="w-4 h-4 text-primary shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0" />
              )}
              <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-body truncate">{item.title}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {TYPE_LABEL[item.type] ?? 'Resource'}
                  {item.read_time_minutes ? ` · ${item.read_time_minutes} min` : ''}
                  {item.is_required ? ' · Required' : ' · Optional'}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <CoachContentViewerModal
        item={viewing ? { ...viewing, id: viewing.content_id } : null}
        onClose={() => {
          setViewing(null);
          reload();
        }}
      />
    </div>
  );
}
